import { parseDate } from "../helpers/parser";

export const EVENTS_COLLECTION = "info_events";
export const ATTENDEES_COLLECTION = "attendees";
export const ATTENDEES_STORE_KEY = "attendees";
export const AttendeeStatus = {
  Unconfirmed: "unconfirmed",
  Attending: "attending",
  NotAttending: "not_attending",
} as const;

const ATTENDEE_STATUS_LABELS: Record<string, string> = {
  attending: "Deltager",
  not_attending: "Deltager ikke",
  unconfirmed: "Afventer svar",
};

const toNumber = (value: unknown): number => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number.parseInt(value, 10)
        : 0;

  return Number.isFinite(parsed) ? parsed : 0;
};

export type EventStatsDatum = {
  name: string;
  attendance: number;
  attending: number;
  not_attending: number;
  unconfirmed: number;
  interviews: number;
  interviews_sport: number;
  interviews_outdoor: number;
  interviews_xoutdoor: number;
  facebook_interested: number;
  facebook_participants: number;
};

export type NextEventStat = {
  label: string;
  nameKey: string;
  value: number;
};

export type AttendeeSummary = {
  total: number;
  attending: number;
  notAttending: number;
  unconfirmed: number;
};

export const getSemesterDateRange = (
  semester: string
): { startAt: Date; endAt: Date } => {
  const year = Number.parseInt(semester, 10);

  return {
    startAt: new Date(`${semester}-09-01T00:00:00`),
    endAt: new Date(`${year + 1}-08-31T23:59:59`),
  };
};

export const mapEventToStats = (infoEvent: InfoEvent): EventStatsDatum => ({
  name: parseDate(infoEvent.date),
  attendance: toNumber(infoEvent.attendance),
  attending: toNumber(infoEvent.attending),
  not_attending: toNumber(infoEvent.not_attending),
  unconfirmed: toNumber(infoEvent.unconfirmed),
  interviews: toNumber(infoEvent.interviews),
  interviews_sport: toNumber(infoEvent.interviews_sport),
  interviews_outdoor: toNumber(infoEvent.interviews_outdoor),
  interviews_xoutdoor: toNumber(infoEvent.interviews_xoutdoor),
  facebook_interested: toNumber(infoEvent.facebook_interested),
  facebook_participants: toNumber(infoEvent.facebook_participants),
});

export const buildNextEventStats = (attendees: Attendee[]): NextEventStat[] => {
  const totals = attendees.reduce<Record<string, number>>(
    (acc, attendee) => {
      if (!(attendee.status in acc)) {
        return acc;
      }

      if (attendee.status === "attending") {
        acc[attendee.status] += toNumber(attendee.participants);
      } else {
        acc[attendee.status] += 1;
      }

      return acc;
    },
    {
      attending: 0,
      not_attending: 0,
      unconfirmed: 0,
    }
  );

  return Object.keys(totals).map((key) => ({
    label: ATTENDEE_STATUS_LABELS[key],
    nameKey: key,
    value: totals[key],
  }));
};

export const summarizeAttendees = (attendees: Attendee[]): AttendeeSummary =>
  attendees.reduce(
    (summary, attendee) => {
      summary.total += 1;

      switch (attendee.status) {
        case AttendeeStatus.Attending:
          summary.attending += toNumber(attendee.participants);
          break;
        case AttendeeStatus.NotAttending:
          summary.notAttending += 1;
          break;
        case AttendeeStatus.Unconfirmed:
        default:
          summary.unconfirmed += 1;
          break;
      }

      return summary;
    },
    {
      total: 0,
      attending: 0,
      notAttending: 0,
      unconfirmed: 0,
    }
  );

export const toSafeNumber = toNumber;
